/**
 * Sprint 6a — alarms, notification rules, working hours and night contacts (ADR 0011).
 *
 * The database triggers decide who is notified; the helpers below implement the SAME rules so the
 * console can show effective (inherited) settings and explain in plain language who receives what.
 * Pure TypeScript: time-zone math uses Intl only, no runtime APIs.
 *
 * ADR 0003: an alarm is a notification and a record, never a command to the vehicle. Nothing here
 * (or anywhere else) offers blocking or stopping a vehicle.
 */
import type { Site, TenantRole } from "./domain.js";
import { siteWithin } from "./operators.js";

export const ALARM_TYPES = [
  "impact",
  "unauthorized_start",
  "card_denied",
  "seat_violation",
  "belt_violation",
  "tamper",
  "power_loss",
  // Sprint 6b (ADR 0015): geofences.
  "geofence_enter",
  "geofence_exit",
  "geofence_dwell",
  // Sprint 7a (ADR 0016): pre-shift checklist. Notifications only, never a vehicle command (ADR 0003).
  "checklist_critical",
  "checklist_missed",
  "checklist_notes",
  // Sprint 7a (ADR 0012, ADR 0017): battery and lift modules.
  "overload",
  "deep_discharge",
  "bat_overtemp",
  // ADR 0027: recorder (m-DVR) health, after a debounce. Notifications only (ADR 0003).
  "dvr_storage",
  "dvr_camera",
  // ADR 0032: a Teltonika unit has a scenario with output control enabled (read-only check). Notification only.
  "device_output_control",
  // ADR 0034: battery voltage below the vehicle's threshold (calibrated analog input), after the ADR 0027
  // debounce. Notification only.
  "battery_low",
] as const;
export type AlarmType = (typeof ALARM_TYPES)[number];

export const ALARM_STATUSES = ["open", "acknowledged"] as const;
export type AlarmStatus = (typeof ALARM_STATUSES)[number];

// push (ADR 0019): one outbox row per recipient device, immediate alarm notifications only.
export const NOTIFICATION_CHANNELS = ["email", "sms", "push"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// maintenance_report (ADR 0016): the checklist report to an external e-mail address (no operator name).
export const NOTIFICATION_KINDS = ["immediate", "digest_item", "document_reminder", "maintenance_report"] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_STATUSES = ["pending", "sending", "sent", "failed", "skipped"] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

/** alarms.ack_note length limit. */
export const ACK_NOTE_MAX = 500;

/** Time zone of working hours and of every time shown in alarms and e-mails. */
export const ALERT_TIME_ZONE = "Europe/Istanbul";

/**
 * One alarm as the console sees it. `operator_uid` (card number) is never sent to the browser;
 * `operatorId` is resolved from it by the database. Ids are strings (alarms.id is a bigint).
 */
export type Alarm = {
  id: string;
  tenantId: string;
  /** Site of the vehicle when the event happened; null when that site was deleted later. */
  siteId: string | null;
  vehicleId: string | null;
  deviceId: string | null;
  eventId: string | null;
  type: AlarmType;
  /** Impact level 1–3; 2 for the other types. */
  severity: number;
  occurredAt: string;
  receivedAt: string | null;
  operatorId: string | null;
  /** The event happened outside the site's working hours. */
  offHours: boolean;
  /** Buffered event that arrived late (always true when `timeUnknown`). */
  delayed: boolean;
  /** The device clock could not be verified: `occurredAt` is the receive time. */
  timeUnknown: boolean;
  /** Same-type repeats on the vehicle folded into this alarm (30-minute rule; never for impacts). */
  repeatCount: number;
  /** Last occurrence (= occurredAt without repeats). */
  lastOccurredAt: string | null;
  /** The notification fan-out failed (nobody was notified automatically); null = fine. */
  notifyError: string | null;
  status: AlarmStatus;
  ackedBy: string | null;
  ackedAt: string | null;
  ackNote: string | null;
  createdAt: string;
  /** Geofence alarms (ADR 0015): the zone, null for other types or when the zone was deleted. */
  geofenceId: string | null;
  /** Violation point of a geofence alarm (null for other types and for the dwell cron without a fix). */
  lat: number | null;
  lon: number | null;
  /** Checklist alarms (ADR 0016): the submission behind checklist_critical / checklist_notes. */
  checklistSubmissionId?: string | null;
  /** checklist_missed: the shift occurrence that had no checklist. */
  shiftName?: string | null;
  shiftStart?: string | null;
};

export const CHECKLIST_ALARM_TYPES = ["checklist_critical", "checklist_missed", "checklist_notes"] as const satisfies readonly AlarmType[];
export const isChecklistAlarmType = (t: string): boolean => (CHECKLIST_ALARM_TYPES as readonly string[]).includes(t);

export const GEOFENCE_ALARM_TYPES = ["geofence_enter", "geofence_exit", "geofence_dwell"] as const satisfies readonly AlarmType[];
/** Alarm types raised by the battery and lift modules (ICD v0.2 section 9). Notification only (ADR 0003). */
export const MODULE_ALARM_TYPES = ["overload", "deep_discharge", "bat_overtemp"] as const satisfies readonly AlarmType[];
/** Alarm types that only exist with a card reader; never raised for a device without one (ADR 0023). */
export const CARD_READER_ALARM_TYPES = ["unauthorized_start", "card_denied"] as const satisfies readonly AlarmType[];
export const isGeofenceAlarmType = (t: string): boolean => (GEOFENCE_ALARM_TYPES as readonly string[]).includes(t);

/** One notification row (no error text or retry internals). */
export type AlarmNotification = {
  id: string;
  tenantId: string;
  alarmId: string | null;
  /** null for a maintenance report to an external e-mail address (`externalRecipientId`). */
  userId: string | null;
  externalRecipientId?: string | null;
  channel: NotificationChannel;
  kind: NotificationKind;
  status: NotificationStatus;
  dueAt: string | null;
  sentAt: string | null;
};

/** Working hours. Days are ISO weekdays (1 = Monday … 7 = Sunday); times are "HH:MM" local to `tz`. */
export type AlertHours = { workDays: number[]; workStart: string; workEnd: string; tz: string };

export type SiteAlertSettings = AlertHours & { tenantId: string; siteId: string };

export type SiteNightContact = { tenantId: string; siteId: string; userId: string };

/** Non-impact alarms of the same type and vehicle within this many minutes of the last one are folded. */
export const ALARM_REPEAT_WINDOW_MIN = 30;

/** "24:00" as a working-day end: the whole day. */
export const END_OF_DAY = "24:00";

export const DEFAULT_ALERT_HOURS: AlertHours = Object.freeze({
  workDays: [1, 2, 3, 4, 5],
  workStart: "08:00",
  workEnd: "18:00",
  tz: ALERT_TIME_ZONE,
}) as AlertHours;

type SiteRef = Pick<Site, "id" | "parentId">;

/** The site and its ancestors, nearest first (cycle-safe). */
function chain(sites: SiteRef[], siteId: string): string[] {
  const byId = new Map(sites.map((s) => [s.id, s]));
  const out: string[] = [];
  let cur: string | null = siteId;
  while (cur && !out.includes(cur) && out.length < 64) {
    out.push(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return out;
}

export type EffectiveHours = {
  hours: AlertHours;
  /** Site whose row applies; null = system defaults. */
  sourceSiteId: string | null;
  /** The site has its own row. */
  own: boolean;
};

/** Settings of the site, else of the nearest ancestor that has a row, else the defaults. */
export function effectiveAlertHours(sites: SiteRef[], settings: SiteAlertSettings[], siteId: string): EffectiveHours {
  for (const id of chain(sites, siteId)) {
    const s = settings.find((x) => x.siteId === id);
    if (s) return { hours: { workDays: [...s.workDays], workStart: s.workStart, workEnd: s.workEnd, tz: s.tz }, sourceSiteId: id, own: id === siteId };
  }
  return { hours: { ...DEFAULT_ALERT_HOURS, workDays: [...DEFAULT_ALERT_HOURS.workDays] }, sourceSiteId: null, own: false };
}

export type EffectiveContacts = { userIds: string[]; sourceSiteId: string | null; own: boolean };

/** Optional checks that mirror the database's night-contact resolution (fts_night_level). */
export type NightContactChecks = {
  /** The contact (still) sees the site; contacts who do not are ignored. Default: everyone. */
  sees?: (userId: string) => boolean;
  /** The contact can receive a notification (has an e-mail address). A level counts only when at least
   * one of its contacts is reachable; unreachable contacts of that level are still listed. Default: everyone. */
  reachable?: (userId: string) => boolean;
};

/**
 * Night contacts of the site, else of the nearest ancestor that has any (with `checks`: any that still
 * sees the site and is reachable); empty when none anywhere.
 */
export function effectiveNightContacts(sites: SiteRef[], contacts: SiteNightContact[], siteId: string, checks: NightContactChecks = {}): EffectiveContacts {
  const sees = checks.sees ?? (() => true);
  const reachable = checks.reachable ?? (() => true);
  for (const id of chain(sites, siteId)) {
    const ids = [...new Set(contacts.filter((c) => c.siteId === id && sees(c.userId)).map((c) => c.userId))];
    if (ids.some(reachable)) return { userIds: ids, sourceSiteId: id, own: id === siteId };
  }
  return { userIds: [], sourceSiteId: null, own: false };
}

/* ---------------- time-zone helpers ---------------- */

/**
 * "08:00" or "08:00:00" (Postgres time) -> minutes after midnight; NaN when malformed. "24:00" (end of
 * day, valid only as a working-day end) is 1440.
 */
export function parseHm(v: string): number {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/.exec(v.trim());
  if (!m) return Number.NaN;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h === 24) return mi === 0 && Number(m[3] ?? 0) === 0 && Number(m[4] ?? 0) === 0 ? 1440 : Number.NaN;
  return h < 24 && mi < 60 ? h * 60 + mi : Number.NaN;
}

/** Working hours cover the whole day (00:00–24:00). */
export const isAllDay = (h: Pick<AlertHours, "workStart" | "workEnd">): boolean => parseHm(h.workStart) === 0 && parseHm(h.workEnd) === 1440;

/** Every day, all day: alarms are never off-hours and there is no morning digest. */
export const isAlwaysWorking = (h: Pick<AlertHours, "workDays" | "workStart" | "workEnd">): boolean =>
  isAllDay(h) && [1, 2, 3, 4, 5, 6, 7].every((d) => h.workDays.includes(d));

/** A valid window: start in [00:00, 24:00), end after start, up to 24:00. */
export function validWorkWindow(start: string, end: string): boolean {
  const s = parseHm(start);
  const e = parseHm(end);
  return !Number.isNaN(s) && !Number.isNaN(e) && s < 1440 && e > s;
}

/** Normalise a Postgres time to "HH:MM". */
export function toHm(v: string): string {
  const n = parseHm(v);
  if (Number.isNaN(n)) return v;
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
}

/** ISO weekday (1 = Monday) of a YYYY-MM-DD calendar date. */
export function isoWeekday(date: string): number {
  const d = new Date(`${date}T12:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

function addDaysTo(date: string, n: number): string {
  return new Date(Date.parse(`${date}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

export type LocalParts = { date: string; isoDay: number; minutes: number };

/** Calendar date, ISO weekday and minutes after midnight of an instant in a time zone. */
export function localParts(at: Date, tz: string): LocalParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  const date = `${get("year")}-${get("month")}-${get("day")}`;
  const hour = Number(get("hour")) % 24;
  return { date, isoDay: isoWeekday(date), minutes: hour * 60 + Number(get("minute")) };
}

function offsetMs(t: number, tz: string): number {
  const p = localParts(new Date(t), tz);
  const [y, m, d] = p.date.split("-").map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, Math.floor(p.minutes / 60), p.minutes % 60);
  return wall - Math.floor(t / 60_000) * 60_000;
}

/** The instant at which the wall clock in `tz` shows `date` `hm` (e.g. Istanbul 2026-10-19 08:00). */
export function zonedTimeToUtc(date: string, hm: string, tz: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const mins = parseHm(hm);
  const guess = Date.UTC(y, m - 1, d, Math.floor(mins / 60), mins % 60);
  let t = guess - offsetMs(guess, tz);
  // A second pass settles instants near a DST change.
  const again = guess - offsetMs(t, tz);
  if (again !== t) t = again;
  return new Date(t);
}

/**
 * Is `at` within working hours? Start is inclusive, end exclusive. If the end is not after the start
 * the window runs past midnight and belongs to the day it starts on (e.g. Mon 22:00 – Tue 06:00).
 * The console only lets users save start < end; the wrap case is handled for robustness.
 */
export function isWorkTime(hours: AlertHours, at: Date): boolean {
  const p = localParts(at, hours.tz);
  const start = parseHm(hours.workStart);
  const end = parseHm(hours.workEnd);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  if (start < end) return hours.workDays.includes(p.isoDay) && p.minutes >= start && p.minutes < end;
  const prevDay = p.isoDay === 1 ? 7 : p.isoDay - 1;
  return (hours.workDays.includes(p.isoDay) && p.minutes >= start) || (hours.workDays.includes(prevDay) && p.minutes < end);
}

/**
 * When the next morning digest goes out: the start of the next working day strictly after `after`.
 * null = no working days, or always working (nothing is ever off-hours).
 */
export function nextDigestAt(hours: AlertHours, after: Date): Date | null {
  if (hours.workDays.length === 0 || Number.isNaN(parseHm(hours.workStart)) || isAlwaysWorking(hours)) return null;
  const today = localParts(after, hours.tz).date;
  for (let i = 0; i <= 8; i++) {
    const day = addDaysTo(today, i);
    if (!hours.workDays.includes(isoWeekday(day))) continue;
    const t = zonedTimeToUtc(day, hours.workStart, hours.tz);
    if (t.getTime() > after.getTime()) return t;
  }
  return null;
}

/** [from, to) instants of local calendar days `fromDay`..`toDay` (inclusive), for date-range filters. */
export function localDayRange(fromDay: string | null, toDay: string | null, tz = ALERT_TIME_ZONE): { from: string | null; to: string | null } {
  return {
    from: fromDay ? zonedTimeToUtc(fromDay, "00:00", tz).toISOString() : null,
    to: toDay ? zonedTimeToUtc(addDaysTo(toDay, 1), "00:00", tz).toISOString() : null,
  };
}

/* ---------------- recipients ---------------- */

/** Minimal member shape for the recipient rules. Empty `siteIds` = the whole tenant. */
export type AlertMember = { userId: string; role: TenantRole; siteIds: string[] };

/** A member sees a site if unrestricted, or restricted to the site or one of its ancestors. */
export function memberSeesSite(sites: SiteRef[], member: Pick<AlertMember, "siteIds">, siteId: string): boolean {
  return member.siteIds.length === 0 || member.siteIds.some((scope) => siteWithin(sites, siteId, scope));
}

/** Day recipients: safety and fleet managers who see the site; if there are none, the tenant admins. */
export function dayRecipients(sites: SiteRef[], members: AlertMember[], siteId: string): string[] {
  const seeing = members.filter((m) => memberSeesSite(sites, m, siteId));
  const day = seeing.filter((m) => m.role === "safety" || m.role === "fleet_manager").map((m) => m.userId);
  return day.length > 0 ? day : members.filter((m) => m.role === "admin").map((m) => m.userId);
}

/** Members who may be chosen as night contacts of a site (console users who see it). */
export function nightContactCandidates(sites: SiteRef[], members: AlertMember[], siteId: string): AlertMember[] {
  return members.filter((m) => memberSeesSite(sites, m, siteId));
}

export type AlarmRouting = {
  /** Notified at once. */
  immediate: string[];
  /** Listed in the next morning digest. */
  digest: string[];
  /** Off-hours impact with no night contacts anywhere: day recipients are notified at once. */
  usedFallback: boolean;
};

/**
 * ADR 0011 routing (same as the fts_alarm_notify trigger). In hours: every alarm goes to the day
 * recipients at once. Off hours: an impact goes to the night contacts at once, and the day recipients
 * who were NOT notified at once see it in the morning digest; with no night contacts it goes to the day
 * recipients at once (no digest) so it is never lost. Other off-hours alarms go to the morning digest
 * only. One rule: nobody gets both an immediate notification and a digest item for the same alarm.
 */
export function routeAlarm(type: AlarmType, offHours: boolean, day: string[], night: string[]): AlarmRouting {
  const uniq = (xs: string[]) => [...new Set(xs)];
  if (!offHours) return { immediate: uniq(day), digest: [], usedFallback: false };
  if (type !== "impact") return { immediate: [], digest: uniq(day), usedFallback: false };
  if (night.length === 0) return { immediate: uniq(day), digest: [], usedFallback: true };
  const immediate = uniq(night);
  return { immediate, digest: uniq(day).filter((u) => !immediate.includes(u)), usedFallback: false };
}
